import { Checkbox } from '#/modules/commonComponents/checkbox';
import { createTipHeader } from '#/modules/commonComponents/table/tableColumnHelpers';

import { cn } from '#/lib/utils';

import { DetailTableExpanderCell } from '#/modules/commonComponents/table/DetailTable';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { sortReceiptPackagesByStockNo } from '#/modules/imsMgmt/receipts/stockReceipt/helper/sortReceiptPackagesByStockNo';
import {
  formatDisplayAmount,
  formatDisplayQty,
} from '#/utils/formatNumber';

function packageQty(pkg) {
  return Number(pkg?.qty ?? pkg?.quantity) || 0;
}

function sumPackageQuantity(packageList = []) {
  return packageList.reduce((sum, pkg) => sum + packageQty(pkg), 0);
}

function getChildInventoryStockIds(lineRow) {
  return (lineRow.packageList ?? [])
    .map((pkg) => pkg.inventoryStockId)
    .filter(Boolean);
}

function resolveParentCheckboxState(childIds, selectedInventoryStockIds) {
  if (childIds.length === 0) {
    return false;
  }

  const selectedCount = childIds.filter((id) => selectedInventoryStockIds.has(id)).length;

  if (selectedCount === 0) {
    return false;
  }

  if (selectedCount === childIds.length) {
    return true;
  }

  return 'indeterminate';
}

/**
 * 進貨單明細欄位（expanding + lead 合欄 checkbox）。
 */
export function buildStockReceiptLineColumns({
  canPrintLabel,
  selectedInventoryStockIds,
  onToggleParent,
  onToggleChild,
}) {
  const leadMeta = { noTruncate: true };

  return [
    {
      id: 'lead',
      size: 56,
      enableSorting: false,
      header: '',
      meta: leadMeta,
      cell: ({ row }) => {
        const isChild = row.depth > 0;

        if (isChild) {
          const stockId = row.original.inventoryStockId;

          return (
            <div className="grid w-full grid-cols-2 items-center">
              <span aria-hidden="true" />
              <div className="flex justify-center">
                {canPrintLabel && stockId ? (
                  <Checkbox
                    checked={selectedInventoryStockIds.has(stockId)}
                    onCheckedChange={(value) => {
                      onToggleChild(stockId, value === true);
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    aria-label="選取庫存標籤"
                  />
                ) : null}
              </div>
            </div>
          );
        }

        return (
          <div className="grid w-full grid-cols-2 items-center">
            <div className="flex justify-center">
              <DetailTableExpanderCell row={row} />
            </div>
            <div className="flex justify-center">
              {canPrintLabel ? (
                <Checkbox
                  checked={resolveParentCheckboxState(
                    getChildInventoryStockIds(row.original),
                    selectedInventoryStockIds,
                  )}
                  onCheckedChange={(value) => {
                    onToggleParent(
                      getChildInventoryStockIds(row.original),
                      value === true,
                    );
                  }}
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  aria-label="選取物料群組"
                />
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      id: 'material',
      header: '物料名稱(代號)',
      size: 220,
      enableSorting: false,
      meta: { cellClassName: 'text-left' },
      cell: ({ row }) => {
        if (row.depth > 0) {
          const stockId = row.original.inventoryStockId;
          const isSelected = stockId && selectedInventoryStockIds.has(stockId);

          return (
            <span
              className={cn(
                'block pl-6 font-mono',
                isSelected ? 'font-semibold text-slate-900' : 'text-slate-500',
              )}
            >
              {row.original.stockNo || '尚未建立庫存編號'}
            </span>
          );
        }

        return (
          <MaterialNameStack
            name={row.original.material?.name}
            code={row.original.material?.mtrNo}
          />
        );
      },
    },
    {
      id: 'unitPrice',
      header: '單價',
      size: 96,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
      cell: ({ row }) => {
        if (row.depth > 0) {
          return null;
        }

        const unitPrice = row.original.unitPrice;
        return (
          <span>{unitPrice != null ? `$${formatDisplayAmount(unitPrice)}` : '-'}</span>
        );
      },
    },
    {
      id: 'amount',
      header: '總金額',
      size: 112,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
      cell: ({ row }) => {
        if (row.depth > 0) {
          return null;
        }

        if (row.original.amount != null) {
          return <span>{formatDisplayQty(row.original.amount)}</span>;
        }

        const totalQty = sumPackageQuantity(row.original.packageList);
        const amount = Number(row.original.unitPrice) * totalQty;

        return <span>{formatDisplayQty(amount)}</span>;
      },
    },
    {
      id: 'totalQuantity',
      header: createTipHeader('總數量', '各包裝數量的加總'),
      size: 96,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
      cell: ({ row }) => {
        if (row.depth > 0) {
          return (
            <span>{formatDisplayQty(packageQty(row.original))}</span>
          );
        }

        return (
          <span>{formatDisplayQty(sumPackageQuantity(row.original.packageList))}</span>
        );
      },
    },
  ];
}

export function flattenInventoryStockIds(lines = []) {
  const packages = (Array.isArray(lines) ? lines : []).flatMap(
    (line) => line.packages ?? [],
  );

  return sortReceiptPackagesByStockNo(packages)
    .map((pkg) => pkg.invStock?.id ?? pkg.inventoryStockId)
    .filter(Boolean);
}
